'use client'

// Next.js
import Link from 'next/link'

// Animation
import { motion } from 'framer-motion'

// design-studio
import {
  Container,
  fadeInUp,
  Section,
  SectionHeader,
  spaceGrotesk,
  staggerContainer,
} from 'design-studio'

const LLM_ONLY = [
  'UI rendering and formatting',
  'CRUD services and wrappers',
  'Data transformations',
  'Simple scripts',
]

const CL_ACTIVATES = [
  'State machines and workflows',
  'Financial and rule engines',
  'Access control and policy systems',
  'Distributed system coordination',
  'Complex integration logic',
]

export function Complexity() {
  return (
    <Section id="complexity" className="relative z-10 py-16 sm:py-24">
      <Container className={`max-w-6xl ${spaceGrotesk.className}`}>
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.2 }}
          className="space-y-10"
        >
          {/* Title + subtitle */}
          <motion.div variants={fadeInUp}>
            <SectionHeader
              pretitle="Scope"
              title={
                <>
                  Tackle Complexity with <br />
                  Math and Logic
                </>
              }
              subtitle="CodeLogician activates when code starts encoding rules, states, or business logic — forcing the agent to translate its understanding into structured logic and using a reasoning engine to surface edge cases, decision boundaries, and invariants that LLMs alone struggle to uncover."
              subtitleClassName="inline-block max-w-[52ch] md:text-base font-normal md:leading-relaxed"
              description="Not all code needs deep reasoning. But systems that encode decisions — payments, access control, workflows, distributed services — create behavioral complexity that statistical AI alone can't fully explore."
            />
          </motion.div>

          {/* Two-column grid */}
          <motion.div
            variants={fadeInUp}
            className="mb-10 grid grid-cols-1 gap-8 pt-4 md:grid-cols-2"
          >
            {/* LLMs Alone */}
            <div className="space-y-5 rounded-xl border border-slate-700/60 bg-slate-800/30 p-6 backdrop-blur-sm">
              <h3 className="text-lg font-semibold text-blue-400">
                LLMs Alone Are Great For
              </h3>
              <ul className="space-y-3">
                {LLM_ONLY.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-base text-slate-300"
                  >
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* CodeLogician Activates */}
            <div className="space-y-5 rounded-xl border border-slate-700/60 bg-slate-800/30 p-6 backdrop-blur-sm">
              <h3 className="text-lg font-semibold text-white">
                CodeLogician Activates For
              </h3>
              <ul className="space-y-3">
                {CL_ACTIVATES.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-base text-white"
                  >
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          <motion.p
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: '-50px 0px' }}
            className="mx-auto max-w-6xl text-base text-slate-300"
          >
            Read the full{' '}
            <Link
              href="/docs/getting-started/tackle-complexity-with-math-and-logic/"
              className="text-blue-400 underline-offset-2 hover:text-blue-300 hover:underline"
            >
              Tackle Complexity with Math and Logic guide here
            </Link>
            , or the{' '}
            <Link
              href="https://medium.com/imandra/managing-complexity-with-math-and-logic-changing-stripe-payment-flow-with-claude-and-codelogician-cdd386001e40"
              target="_blank"
              rel="noopener noreferrer"
              className="underline-offset-2 hover:underline"
              style={{ color: '#a78bfa' }}
            >
              Managing Complexity with Math and Logic article on Medium
            </Link>
            .
          </motion.p>
        </motion.div>
      </Container>
    </Section>
  )
}
